import { Base } from '../base/index.js';
import { trapFocus } from '../../lib/trap-focus.js';

/**
 * Component's name.
 * @type {string}
 */
export const name = 'navbar';

/**
 * Class Navbar.
 */
export class Navbar extends Base {
  /**
   * @type {import('../../types.d.ts').BaseConfig}
   */
  static config = {
    name,
  };

  connectedCallback() {
    this.getElements();
    this.isOpen = false;
  }

  /**
   * getElements
   */
  getElements() {
    /** @type {HTMLButtonElement} */
    this.btn = this.querySelector('.topheader__burger');
    /** @type {HTMLElement} */
    this.panel = this.querySelector('.navbar__content');

    if (this.panel && !this.panel.hasAttribute('tabindex')) {
      this.panel.setAttribute('tabindex', '-1');
    }

    this.btn.addEventListener('click', this.onBtnClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
  }

  /**
   * onClick
   */
  onBtnClick() {
    if (this.isOpen) {
      return this.close();
    }

    return this.open();
  }

  /**
   * Open
   */
  open() {
    document.body.classList.add('overflow-hidden');
    this.classList.add('is-open');
    this.isOpen = true;
    this.btn.setAttribute('aria-expanded', 'true');

    // `.navbar__content` is only an actual modal dialog while open on
    // mobile: at desktop widths it becomes the always-visible menu bar
    // (see navbar/index.css, min-width: 1100px), so role/aria-modal must
    // not be hardcoded in the markup — that would tell screen readers the
    // rest of the page is unavailable on every desktop page load.
    this.panel?.setAttribute('role', 'dialog');
    this.panel?.setAttribute('aria-modal', 'true');

    this.panel?.focus();
  }

  /**
   * Close
   *
   * Always restores focus to the burger button itself (its own trigger is
   * unambiguous), rather than relying on the shared saveActiveElement/
   * untrapFocus module state — nested `unistra-menu-item`s also use that
   * shared state for their own open/close cycle, so depending on it here
   * too could leave focus stranded on a now-hidden nested element.
   */
  close() {
    if (!this.isOpen) {
      // Search.js calls nav.close() unconditionally whenever the search
      // modal opens, even when the mobile nav wasn't open — don't steal
      // focus to the burger button in that case.
      return;
    }

    document.body.classList.remove('overflow-hidden');
    this.classList.remove('is-open');
    this.isOpen = false;
    this.btn.setAttribute('aria-expanded', 'false');

    this.panel?.removeAttribute('role');
    this.panel?.removeAttribute('aria-modal');

    this.btn.focus();
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (!this.isOpen || !this.panel) {
      return;
    }

    trapFocus(this.panel, event);
  }

  /**
   * @param {object} event
   * @param {string} event.key
   */
  onKeyup({ key }) {
    if (this.isOpen && key === 'Escape') {
      this.close();
    }
  }
}

export default Navbar;
